import React, { FC, useCallback, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { useTestDescriptions } from "@app/components/common/testDescription";
import { TestType } from "@app/provider/testData/types";

import "./publicGisTriage.scss";

const messages = defineMessages({
  pageTitle: {
    id: "publicGisOrderSelect.pageTitle",
    defaultMessage:
      "Please select which test you're requesting a genetic information session for.",
  },
  orderedOn: {
    id: "publicGisOrderSelect.orderedOn",
    defaultMessage: "Ordered {date}",
  },
  next: {
    id: "publicGisOrderSelect.next",
    defaultMessage: "Next",
  },
  back: {
    id: "publicGisOrderSelect.back",
    defaultMessage: "Back",
  },
});

/**
 * Human-readable product label for an order. Most GIS-eligible product types
 * are brand names, so a title-cased enum reads correctly (SIGNATERA →
 * Signatera). A small override map covers acronyms that shouldn't be
 * title-cased.
 */
const TEST_TYPE_LABEL_OVERRIDES: Partial<Record<TestType, string>> = {
  [TestType.DPYD]: "DPYD",
};

export function formatTestTypeLabel(testType: TestType | string): string {
  return (
    TEST_TYPE_LABEL_OVERRIDES[testType as TestType] ??
    testType.charAt(0).toUpperCase() + testType.slice(1).toLowerCase()
  );
}

/**
 * Normalized order shape for the picker. `id` is an opaque selection key the
 * caller maps back to its own order model (test-card uid for the authenticated
 * flow, order/test uid for the anonymous guest flow).
 */
export interface GisOrderPickerOption {
  id: string;
  testType: TestType | string;
  createdAt?: string | Date | null;
}

interface GisOrderPickerProps {
  orders: GisOrderPickerOption[];
  onSelect: (id: string) => void;
  onBack: () => void;
  backLabel?: string;
}

/**
 * Presentational multi-order picker for the GIS scheduling funnels. Owns only
 * the selection UI (radio list + Next/Back) and is intentionally free of any
 * data-fetching, routing, or auth coupling so it can be shared by both the
 * authenticated `PublicGisOrderSelect` and the anonymous order-lookup guest
 * funnel. Callers supply the eligible orders and handle selection / back.
 */
const GisOrderPicker: FC<GisOrderPickerProps> = ({
  orders,
  onSelect,
  onBack,
  backLabel,
}) => {
  const intl = useIntl();
  const testDescriptions = useTestDescriptions();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const hasSelection = useMemo(
    () => orders.some((order) => order.id === selectedId),
    [orders, selectedId]
  );

  const handleNext = useCallback(() => {
    if (!selectedId || !hasSelection) return;
    onSelect(selectedId);
  }, [hasSelection, onSelect, selectedId]);

  return (
    <div className="public-gis-triage">
      <div className="public-gis-triage__select">
        <h1 className="public-gis-triage__title">
          {intl.formatMessage(messages.pageTitle)}
        </h1>
        <fieldset className="public-gis-triage__order-group">
          <legend className="public-gis-triage__visually-hidden">
            {intl.formatMessage(messages.pageTitle)}
          </legend>
          {orders.map((order) => {
            const isSelected = order.id === selectedId;
            const description =
              testDescriptions[order.testType as TestType] ||
              (order.createdAt
                ? intl.formatMessage(messages.orderedOn, {
                    date: intl.formatDate(new Date(order.createdAt), {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }),
                  })
                : "");
            return (
              <label key={order.id} className="public-gis-triage__order-option">
                <input
                  type="radio"
                  name="gis-order"
                  className="public-gis-triage__order-radio"
                  value={order.id}
                  checked={isSelected}
                  onChange={() => setSelectedId(order.id)}
                />
                <span className="public-gis-triage__order-text">
                  <span className="public-gis-triage__order-name">
                    {formatTestTypeLabel(order.testType)}
                  </span>
                  {description && (
                    <span className="public-gis-triage__order-desc">
                      {description}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </fieldset>
        <div className="public-gis-triage__actions-stacked">
          <button
            type="button"
            className="public-gis-triage__primary-btn"
            disabled={!hasSelection}
            onClick={handleNext}
          >
            {intl.formatMessage(messages.next)}
          </button>
          <button
            type="button"
            className="public-gis-triage__back-btn"
            onClick={onBack}
          >
            {backLabel ?? intl.formatMessage(messages.back)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GisOrderPicker;
